import { useState, useContext, useEffect, FC } from "react";
import * as React from "react";

import { Button, IconLinkButton } from "src/components/buttons";

import OrganizationAPI from "./API/OrganizationAPI";
import { Tooltip } from "antd";

import { Monitor } from "lucide-react";
import { Icon } from "src/components/icons/Icon";

import PickListContext from "src/context/pickListContext";
import { DataTable, PageHero, Page } from "src/components/common";
import AuthorizationWrapper from "src/common/AuthorizationWrapper";
import {
	setStatusCellValue,
	filterByStatus,
	StatusFilterMethod,
} from "src/components/forms/StatusFilter";
import {
	ClearFilterButton,
	ActionIcon,
} from "src/components/forms/ActionColumnIconsAndButton";
import useGetDataHook from "src/admin/Organization/Courses/useGetDataHook";
import DatasetContext from "src/context/DatasetContext";
import { AdminPermissions } from "src/common/Permissions";
import ClientAppConstants from "src/common/Constants";

interface IProps {
	currentUser: CurrentUserDTO;
	match?: any;
	history?: any;
}

const OrganizationListPage: FC<IProps> = ({ currentUser, history }) => {
	const [{ data: organizations, isLoading }] = useGetDataHook<
		OrganizationDTO[]
	>([], {
		apiCallFunc: OrganizationAPI.getOrganizations,
		params: [],
	});
	const [filtered, setFiltered] = useState<any>([]);
	const { pickListItems } = useContext(PickListContext);
	const { updateCurrentOrganization } = useContext(DatasetContext);

	useEffect(() => {
		setFiltered([{ id: "isActive", value: "Active" }]);
	}, []);

	const getActionLink = (rowId: any): string => {
		return `/admin/organizations/${rowId}`;
	};

	const onRowClick = (row: any) => {
		history.push(getActionLink(row.original.id));
	};

	const getColumns = () => {
		const onClientsiteButtonClick = (rowId: any) => () => {
			updateCurrentOrganization(rowId);
		};

		const shouldShowNavigateButton = (row: any) => {
			if (
				currentUser.permissions!.includes(AdminPermissions.GlobalAdmin) ||
				(currentUser.datasetPermissions &&
					currentUser.datasetPermissions.length > 0)
			) {
				const linkTo = currentUser.permissions!.includes(
					AdminPermissions.GlobalAdmin
				)
					? `/organizations/${row.value}/datasets/admin`
					: `/organizations/${row.value}/datasets`;
				return (
					<IconLinkButton
						className="pl-0"
						onClick={onClientsiteButtonClick(row.value)}
						to={linkTo}
						title={`Go to client site`}
					>
						<Icon size="2x" icon={Monitor} />
					</IconLinkButton>
				);
			} else {
				return null;
			}
		};

		return [
			{
				Header: <Tooltip title="Web Id Prefix">Web Id Prefix</Tooltip>,
				accessor: "webIdPrefix",
				filterable: true,
			},
			{
				Header: <Tooltip title="Organization">Organization</Tooltip>,
				accessor: "name",
				filterable: true,
			},
			{
				Header: <Tooltip title="Active">Active</Tooltip>,
				accessor: "isActive",
				Cell: setStatusCellValue,
				filterMethod: filterByStatus("isActive"),
				Filter: StatusFilterMethod,
			},
			{
				Header: <Tooltip title="Actions">Actions</Tooltip>,
				accessor: "id",
				Filter: (
					<ClearFilterButton
						onClick={() => setFiltered([{ id: "isActive", value: "Active" }])}
					/>
				),
				Cell: (row: any) => (
					<>
						<ActionIcon size={"2x"} title={`Details`} type="open" />

						{shouldShowNavigateButton(row)}
					</>
				),
			},
		];
	};
	const columns = getColumns();
	const displayableOrgs = organizations.filter(
		(o) => o.id !== ClientAppConstants.DefaultOrgId
	); // hide default org in list
	if (pickListItems.length > 0) {
		return (
			<Page title="Organizations">
				<PageHero className="d-flex">
					<h1 className="flex-fill mb-0 pr-3">Organizations</h1>
					<AuthorizationWrapper
						type="hide"
						permissions={[AdminPermissions.EditOrganization]}
					>
						<Button className="btn-primary" to={`/admin/organizations/0`}>
							<span style={{ textTransform: "capitalize" }}>
								New Organization
							</span>
						</Button>
					</AuthorizationWrapper>
				</PageHero>
				<div className="container-fluid">
					<DataTable
						data={displayableOrgs}
						filtered={filtered}
						columns={columns}
						filterable={true}
						noDataText={`No organizations found`}
						onFilteredChange={(newFiltered: any) => setFiltered(newFiltered)}
						loading={isLoading}
						onRowClick={onRowClick}
						storageKey={"OrgList"}
					/>
				</div>
			</Page>
		);
	}
	return null;
};

export default OrganizationListPage;
